import {UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';

import {useLazyCalculateFromWithoutVatQuery} from '@dms/api/core';
import {CountryAlpha3CodeEnum, VatRateType} from '@dms/api/shared';
import {handleApiError} from '@dms/shared';

import {Nullish, precisionCalculation} from 'shared/web';

import {DeliveryNoteDocumentPreviewFormDefinition} from '../types/DeliveryNoteDocumentPreviewFormDefinition';
import {ItemPriceSource} from '../types/ItemPriceSource';

const VAT_RATE_TYPES = ['S', 'Z', 'E', 'R', 'MR', 'SR', 'P'] satisfies readonly VatRateType[];

function isVatRateType(value: string): value is VatRateType {
  return VAT_RATE_TYPES.some((rate) => rate === value);
}

interface RecalculateItemPriceParams {
  index: number;
  source: ItemPriceSource;
  quantity: number | Nullish;
  price: number | Nullish;
  skipNonPositivePrices?: boolean;
}

export function useRecalculateItemPrice(
  formApi: UseFormReturn<DeliveryNoteDocumentPreviewFormDefinition>,
  countryCode: CountryAlpha3CodeEnum
) {
  const [calculatePriceWithoutVat] = useLazyCalculateFromWithoutVatQuery();

  return (params: RecalculateItemPriceParams) => {
    const vat = formApi.getValues(`listOfItems.${params.index}.vat`);

    if (
      isNil(params.quantity) ||
      isNil(params.price) ||
      (params.source === 'totalPrice' && params.quantity === 0)
    ) {
      return true;
    }

    if (isNil(vat) || !isVatRateType(vat)) {
      return false;
    }

    const unitPriceWithoutVat = precisionCalculation.round(
      params.source === 'unitPrice'
        ? params.price
        : precisionCalculation.divide(params.price, params.quantity),
      2
    );
    const totalPriceWithoutVat = precisionCalculation.round(
      params.source === 'totalPrice'
        ? params.price
        : precisionCalculation.multiply(params.quantity, params.price),
      2
    );

    if (params.skipNonPositivePrices && (unitPriceWithoutVat <= 0 || totalPriceWithoutVat <= 0)) {
      return true;
    }

    calculatePriceWithoutVat({
      amount: totalPriceWithoutVat.toString(),
      vatRateType: vat,
      countryCode,
    })
      .unwrap()
      .then((result) => {
        const currentItem = formApi.getValues(`listOfItems.${params.index}`);
        const currentSourcePrice =
          params.source === 'unitPrice' ? currentItem.unitPrice : currentItem.totalPrice;

        if (
          currentItem.quantity !== params.quantity ||
          currentItem.vat !== vat ||
          currentSourcePrice !== params.price
        ) {
          return;
        }

        formApi.setValue(`listOfItems.${params.index}.unitPrice`, unitPriceWithoutVat);
        formApi.setValue(
          `listOfItems.${params.index}.totalPrice`,
          precisionCalculation.round(Number(result.withoutVat.replace(',', '.')), 2)
        );
      })
      .catch(handleApiError);

    return true;
  };
}
